import { Box, Divider, Flex } from "@chakra-ui/react";
import { useContext, useEffect } from "react";
import {
  DurationDistance,
  InsertMidSpotDirection,
  SpotType,
} from "~/generated/graphql.types";
import { EditMode, EditModeContext } from "../../..";
import { SpotForSidebar } from "../convertType";
import { DeleteOptionalSpots } from "./DeleteOptionalSpots";
import { InsertSpotButton } from "./InsertSpotButton";
import { SpotStatInterface } from "./SpotStat";

export const BetweenTwoSpotListItems = ({
  prevSpot,
  nextSpot,
  allSpotsStats,
  withoutOptionalSpotsStats,
  test,
}: {
  prevSpot: SpotForSidebar;
  nextSpot: SpotForSidebar;
  allSpotsStats: DurationDistance | null | undefined;
  withoutOptionalSpotsStats: DurationDistance | null | undefined;
  test?: Map<string, { durationDistance: DurationDistance | null | undefined }>;
}) => {
  const { editMode } = useContext(EditModeContext);

  const prevSpotParentSpotId = prevSpot.parentSpotId;
  const nextSpotParentSpotId = nextSpot.parentSpotId;
  const sameParentSpotId = prevSpotParentSpotId === nextSpotParentSpotId;

  const Wrapper =
    prevSpotParentSpotId && nextSpotParentSpotId
      ? sameParentSpotId
        ? PrevHasAndNextHasAndSameParent
        : PrevHasAndNextHasAndDifferentParent
      : prevSpotParentSpotId && !nextSpotParentSpotId
      ? PrevHasAndNextNot
      : !prevSpotParentSpotId && nextSpotParentSpotId
      ? PrevNotAndNextHas
      : !prevSpotParentSpotId && !nextSpotParentSpotId
      ? PrevNotAndNextNot
      : Box;
  return (
    <Wrapper
      prevSpot={prevSpot}
      nextSpot={nextSpot}
      allSpotsStats={allSpotsStats}
      withoutOptionalSpotsStats={withoutOptionalSpotsStats}
      editMode={editMode}
    />
  );
};

const PrevHasAndNextHasAndSameParent = ({
  prevSpot,
  nextSpot,
  allSpotsStats,
  withoutOptionalSpotsStats,
  editMode,
}: {
  prevSpot: SpotForSidebar;
  nextSpot: SpotForSidebar;
  allSpotsStats: DurationDistance | null | undefined;
  withoutOptionalSpotsStats: DurationDistance | null | undefined;
  editMode: EditMode;
}) => {
  return (
    <Flex alignItems="center" justifyContent={"space-between"} w="full">
      <SpotStatInterface stat={allSpotsStats} hasLeftMargin={true} />
      <InsertSpotButton
        input={{
          targetSpotId: prevSpot.id,
          courseId: prevSpot.courseId,
          direction: InsertMidSpotDirection.DownSide,
          parentSpotId: prevSpot.parentSpotId,
          spotType:
            editMode === "contents"
              ? SpotType.Narration
              : SpotType.RoutingGuide,
        }}
      />
    </Flex>
  );
};

const PrevHasAndNextHasAndDifferentParent = ({
  prevSpot,
  nextSpot,
  allSpotsStats,
  withoutOptionalSpotsStats,
  editMode,
}: {
  prevSpot: SpotForSidebar;
  nextSpot: SpotForSidebar;
  allSpotsStats: DurationDistance | null | undefined;
  withoutOptionalSpotsStats: DurationDistance | null | undefined;
  editMode: EditMode;
}) => {
  return (
    <>
      <Flex alignItems="center" justifyContent={"space-between"} w="full">
        <SpotStatInterface stat={allSpotsStats} hasLeftMargin={true} />
        <InsertSpotButton
          input={{
            targetSpotId: prevSpot.id,
            courseId: prevSpot.courseId,
            direction: InsertMidSpotDirection.DownSide,
            parentSpotId: prevSpot.parentSpotId,
            spotType:
              editMode === "contents"
                ? SpotType.Narration
                : SpotType.RoutingGuide,
          }}
        />
      </Flex>
      <Divider my={2} />
      <Flex alignItems="center" justifyContent={"space-between"} w="full">
        <SpotStatInterface
          stat={withoutOptionalSpotsStats}
          hasLeftMargin={false}
        />
        <InsertSpotButton
          input={{
            targetSpotId: prevSpot.id,
            courseId: prevSpot.courseId,
            direction: InsertMidSpotDirection.DownSideSpecialProcessing,
            parentSpotId: undefined,
            spotType:
              editMode === "contents" ? SpotType.Narration : SpotType.Waypoint,
          }}
        />
      </Flex>
      <Divider my={2} />
      {editMode === "routing" ? (
        <DeleteOptionalSpots
          courseId={nextSpot.courseId}
          parentSpotId={nextSpot.parentSpotId!}
        />
      ) : undefined}
      <Flex alignItems="center" justifyContent={"space-between"} w="full">
        <SpotStatInterface stat={allSpotsStats} hasLeftMargin={true} />
        <InsertSpotButton
          input={{
            targetSpotId: nextSpot.id,
            courseId: nextSpot.courseId,
            direction: InsertMidSpotDirection.UpSide,
            parentSpotId: nextSpot.parentSpotId,
            spotType:
              editMode === "contents"
                ? SpotType.Narration
                : SpotType.RoutingGuide,
          }}
        />
      </Flex>
    </>
  );
};

const PrevHasAndNextNot = ({
  prevSpot,
  nextSpot,
  allSpotsStats,
  withoutOptionalSpotsStats,
  editMode,
}: {
  prevSpot: SpotForSidebar;
  nextSpot: SpotForSidebar;
  allSpotsStats: DurationDistance | null | undefined;
  withoutOptionalSpotsStats: DurationDistance | null | undefined;
  editMode: EditMode;
}) => {
  return (
    <>
      <Flex alignItems="center" justifyContent={"space-between"} w="full">
        <SpotStatInterface stat={allSpotsStats} hasLeftMargin={true} />
        <InsertSpotButton
          input={{
            targetSpotId: prevSpot.id,
            courseId: prevSpot.courseId,
            direction: InsertMidSpotDirection.DownSide,
            parentSpotId: prevSpot.parentSpotId,
            spotType:
              editMode === "contents"
                ? SpotType.Narration
                : SpotType.RoutingGuide,
          }}
        />
      </Flex>
      <Divider my={2} />
      <Flex alignItems="center" justifyContent={"space-between"} w="full">
        <SpotStatInterface
          stat={withoutOptionalSpotsStats}
          hasLeftMargin={false}
        />
        <InsertSpotButton
          input={{
            targetSpotId: nextSpot.id,
            courseId: nextSpot.courseId,
            direction: InsertMidSpotDirection.UpSide,
            parentSpotId: undefined,
            spotType:
              editMode === "contents" ? SpotType.Narration : SpotType.Waypoint,
          }}
        />
      </Flex>
    </>
  );
};

const PrevNotAndNextHas = ({
  prevSpot,
  nextSpot,
  allSpotsStats,
  withoutOptionalSpotsStats,
  editMode,
}: {
  prevSpot: SpotForSidebar;
  nextSpot: SpotForSidebar;
  allSpotsStats: DurationDistance | null | undefined;
  withoutOptionalSpotsStats: DurationDistance | null | undefined;
  editMode: EditMode;
}) => {
  return (
    <>
      <Flex alignItems="center" justifyContent={"space-between"} w="full">
        <SpotStatInterface
          stat={withoutOptionalSpotsStats}
          hasLeftMargin={false}
        />
        <InsertSpotButton
          input={{
            targetSpotId: prevSpot.id,
            courseId: prevSpot.courseId,
            direction: InsertMidSpotDirection.DownSide,
            parentSpotId: undefined,
            spotType:
              editMode === "contents" ? SpotType.Narration : SpotType.Waypoint,
          }}
        />
      </Flex>
      <Divider my={2} />
      {editMode === "routing" ? (
        <DeleteOptionalSpots
          courseId={nextSpot.courseId}
          parentSpotId={nextSpot.parentSpotId!}
        />
      ) : undefined}

      <Flex alignItems="center" justifyContent={"space-between"} w="full">
        <SpotStatInterface stat={allSpotsStats} hasLeftMargin={true} />
        <InsertSpotButton
          input={{
            targetSpotId: nextSpot.id,
            courseId: nextSpot.courseId,
            direction: InsertMidSpotDirection.UpSide,
            parentSpotId: nextSpot.parentSpotId,
            spotType:
              editMode === "contents"
                ? SpotType.Narration
                : SpotType.RoutingGuide,
          }}
        />
      </Flex>
    </>
  );
};

const PrevNotAndNextNot = ({
  prevSpot,
  nextSpot,
  allSpotsStats,
  withoutOptionalSpotsStats,
  editMode,
}: {
  prevSpot: SpotForSidebar;
  nextSpot: SpotForSidebar;
  allSpotsStats: DurationDistance | null | undefined;
  withoutOptionalSpotsStats: DurationDistance | null | undefined;
  editMode: EditMode;
}) => {
  return (
    <Flex alignItems="center" justifyContent={"space-between"} w="full">
      <SpotStatInterface
        stat={withoutOptionalSpotsStats}
        hasLeftMargin={false}
      />
      <InsertSpotButton
        input={{
          targetSpotId: prevSpot.id,
          courseId: prevSpot.courseId,
          direction: InsertMidSpotDirection.DownSide,
          parentSpotId: undefined,
          spotType:
            editMode === "contents" ? SpotType.Narration : SpotType.Waypoint,
        }}
      />
    </Flex>
  );
};
